import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Sun, Clock, Shield, ShieldCheck, ClipboardList, Moon, Check, X, AlertTriangle, CalendarDays, FileText, GraduationCap, Receipt, Upload, Plus, Trash2, Camera, IdCard, Send } from 'lucide-react';
import api from '../lib/api';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const EXPENSE_CATEGORIES = [
  { value: 'carburante', label: 'Carburante' },
  { value: 'parcheggio', label: 'Parcheggio' },
  { value: 'pedaggi', label: 'Pedaggi' },
  { value: 'pasti', label: 'Pasti' },
  { value: 'alloggio', label: 'Alloggio' },
  { value: 'materiali', label: 'Materiali' },
  { value: 'attrezzature', label: 'Attrezzature' },
  { value: 'trasporti', label: 'Trasporti' },
  { value: 'altro', label: 'Altro' },
];

const EMPTY_CLAIM = {
  date: new Date().toISOString().split('T')[0],
  amount: '',
  category: 'altro',
  description: '',
  receipt_number: '',
  site_id: '',
  notes: '',
};

const WEEKDAY_LABELS = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

function buildCalendarWeeks(period, snapshot) {
  const [year, month] = period.split('-').map(Number);
  const byDate = Object.fromEntries(snapshot.map((s) => [s.date, s]));
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push(byDate[dateStr] || { date: dateStr, _future: true });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function dayEditTotal(e) {
  if (!e) return 0;
  return (parseFloat(e.ordinarie) || 0) + (parseFloat(e.straordinarie) || 0) + (parseFloat(e.notturne) || 0);
}

function HoursConfirmationCard({ item, onSubmitted }) {
  const [edits, setEdits] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setEdit = (date, field, value) => {
    setEdits((e) => ({ ...e, [date]: { ...e[date], [field]: value } }));
  };
  const clearEdit = (date) => {
    setEdits((e) => { const n = { ...e }; delete n[date]; return n; });
  };

  const submit = async () => {
    const changedDates = Object.keys(edits).filter((d) => dayEditTotal(edits[d]) > 0);
    const corrections = changedDates.map((d) => {
      const cell = item.snapshot.find((s) => s.date === d);
      return {
        date: d,
        hours_ordinarie: parseFloat(edits[d].ordinarie) || 0,
        hours_straordinarie: parseFloat(edits[d].straordinarie) || 0,
        hours_notturne: parseFloat(edits[d].notturne) || 0,
        note: (edits[d].note || '').trim(),
        _wasRecorded: cell?.has_record,
      };
    });
    const missing = corrections.find((c) => c._wasRecorded && !c.note);
    if (missing) {
      alert(`Scrivi una motivazione per il giorno ${formatDate(missing.date)}: era gia' registrato con un valore diverso.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = corrections.map(({ _wasRecorded, ...rest }) => rest);
      await api.post(`/hours-confirmation/${item.id}/respond`, { confirmed: payload.length === 0, corrections: payload });
      onSubmitted();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore invio risposta");
    } finally {
      setSubmitting(false);
    }
  };

  if (item.status !== 'pending') {
    const isConfirmed = item.status === 'confirmed';
    return (
      <Card className="border-slate-200 shadow-sm" data-testid={`hours-item-${item.id}`}>
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-800">Ore di {item.period}</p>
            <p className="text-xs text-slate-500">{item.total_hours}h registrate</p>
          </div>
          <Badge className={isConfirmed ? 'bg-green-600' : 'bg-amber-600'}>{isConfirmed ? 'Confermate' : 'Correzioni inviate'}</Badge>
        </CardContent>
        {item.status === 'disputed' && item.corrections?.length > 0 && (
          <CardContent className="pt-0 space-y-1 text-xs text-slate-600">
            {item.corrections.map((c, i) => (
              <p key={i}>
                <b>{formatDate(c.date)}</b>: {c.hours_ordinarie || 0}h ord.
                {c.hours_straordinarie ? ` + ${c.hours_straordinarie}h straord.` : ''}
                {c.hours_notturne ? ` + ${c.hours_notturne}h nott.` : ''}
                {c.note ? ` — ${c.note}` : ''}
              </p>
            ))}
          </CardContent>
        )}
      </Card>
    );
  }

  const weeks = buildCalendarWeeks(item.period, item.snapshot);
  const selectedCell = selectedDate ? item.snapshot.find((s) => s.date === selectedDate) : null;
  const missingCount = item.snapshot.filter((s) => !s.has_record).length;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`hours-item-${item.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-1">
          <p className="text-sm font-semibold text-slate-800">Ore di {item.period}</p>
          <span className="text-xs text-slate-500">{item.total_hours}h registrate{missingCount > 0 && ` · ${missingCount} giorni senza timbratura`}</span>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-50 border border-green-300 inline-block" /> Registrato</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-50 border border-amber-300 inline-block" /> Manca timbratura</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-100 border border-blue-400 inline-block" /> Modificato da te</span>
        </div>

        <div className="max-w-md">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-slate-400 mb-1">
            {WEEKDAY_LABELS.map((l, i) => <div key={i}>{l}</div>)}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-1 mb-1">
              {week.map((cell, ci) => {
                if (!cell || cell._future) return <div key={ci} className="h-11 rounded-md bg-slate-50" />;
                const dayNum = Number(cell.date.slice(-2));
                const edited = edits[cell.date];
                const editedTotal = dayEditTotal(edited);
                const cls = editedTotal > 0
                  ? 'bg-blue-100 border-blue-400 text-blue-800'
                  : cell.has_record
                    ? 'bg-green-50 border-green-300 text-green-800'
                    : 'bg-amber-50 border-amber-300 text-amber-700';
                return (
                  <button key={ci} type="button" onClick={() => setSelectedDate(cell.date)}
                    className={`h-11 rounded-md border flex flex-col items-center justify-center text-[10px] leading-tight hover:opacity-80 ${cls} ${selectedDate === cell.date ? 'ring-2 ring-offset-1 ring-[#1a4b8c]' : ''}`}
                    data-testid={`hours-cell-${item.id}-${cell.date}`}>
                    <span>{dayNum}</span>
                    <span className="font-semibold">{editedTotal > 0 ? `${editedTotal}h` : cell.has_record ? `${cell.hours_worked}h` : '—'}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {selectedCell && (
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-800">
                {formatDate(selectedCell.date)}{selectedCell.has_record ? ` — registrate ${selectedCell.hours_worked}h` : ' — nessuna timbratura'}
              </p>
              <button type="button" onClick={() => setSelectedDate(null)} className="text-xs text-slate-400 hover:text-slate-600">Chiudi</button>
            </div>
            <p className="text-[11px] text-slate-400">Se hai lavorato anche di notte (es. turno che entra nella notte), indica separatamente le ore notturne — servono per calcolare correttamente la busta paga.</p>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <Label className="text-xs">Ordinarie</Label>
                <Input type="number" step="0.25" min="0" className="w-20 h-8"
                  value={edits[selectedCell.date]?.ordinarie ?? ''}
                  onChange={(e) => setEdit(selectedCell.date, 'ordinarie', e.target.value)}
                  data-testid={`hours-ord-${item.id}-${selectedCell.date}`} />
              </div>
              <div>
                <Label className="text-xs">Straordinarie</Label>
                <Input type="number" step="0.25" min="0" className="w-20 h-8"
                  value={edits[selectedCell.date]?.straordinarie ?? ''}
                  onChange={(e) => setEdit(selectedCell.date, 'straordinarie', e.target.value)}
                  data-testid={`hours-straord-${item.id}-${selectedCell.date}`} />
              </div>
              <div>
                <Label className="text-xs">Notturne</Label>
                <Input type="number" step="0.25" min="0" className="w-20 h-8"
                  value={edits[selectedCell.date]?.notturne ?? ''}
                  onChange={(e) => setEdit(selectedCell.date, 'notturne', e.target.value)}
                  data-testid={`hours-nott-${item.id}-${selectedCell.date}`} />
              </div>
              {edits[selectedCell.date] && (
                <button type="button" onClick={() => clearEdit(selectedCell.date)} className="text-xs text-slate-400 hover:text-red-600 h-8">Annulla</button>
              )}
            </div>
            <div>
              <Label className="text-xs">Motivazione {selectedCell.has_record ? '(obbligatoria)' : '(facoltativa)'}</Label>
              <Input value={edits[selectedCell.date]?.note ?? ''}
                onChange={(e) => setEdit(selectedCell.date, 'note', e.target.value)}
                placeholder={selectedCell.has_record ? "perche' e' diverso..." : 'es. timbratura dimenticata, turno notturno 22-02...'}
                className="h-8" data-testid={`hours-note-${item.id}-${selectedCell.date}`} />
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={submit} disabled={submitting} data-testid={`hours-submit-${item.id}`}>
            <Send size={14} className="mr-1" /> {submitting ? 'Invio...' : Object.keys(edits).length > 0 ? 'Invia correzioni' : 'Confermo, tutto corretto'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmployeeAreaPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'ferie');
  const [balance, setBalance] = useState(null);
  const [points, setPoints] = useState(null);
  const [reports, setReports] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [pendingOncall, setPendingOncall] = useState([]);
  const [myOncall, setMyOncall] = useState([]);
  const [expenseClaims, setExpenseClaims] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimForm, setClaimForm] = useState(EMPTY_CLAIM);
  const [receiptFile, setReceiptFile] = useState(null);
  const [savingClaim, setSavingClaim] = useState(false);
  const [myDocs, setMyDocs] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [pendingIdentity, setPendingIdentity] = useState(null);
  const [hoursConfirmations, setHoursConfirmations] = useState([]);

  const eid = user?.employee_id;

  const load = async () => {
    if (!eid) { setLoading(false); return; }
    try {
      const [balRes, ptRes, repRes, lvRes, pendRes, oncRes, claimsRes, sitesRes, docsRes, hoursRes] = await Promise.all([
        api.get(`/leave-balances/employee/${eid}`).catch(() => ({ data: null })),
        api.get(`/safety-points/employee/${eid}`).catch(() => ({ data: null })),
        api.get(`/employee-reports/by-employee/${eid}`).catch(() => ({ data: [] })),
        api.get('/leaves').catch(() => ({ data: [] })),
        api.get('/oncall/my-pending').catch(() => ({ data: [] })),
        api.get('/oncall', { params: { employee_id: eid } }).catch(() => ({ data: [] })),
        api.get('/expense-claims').catch(() => ({ data: [] })),
        api.get('/sites').catch(() => ({ data: [] })),
        api.get('/personal-docs/my').catch(() => ({ data: [] })),
        api.get('/hours-confirmation/my').catch(() => ({ data: [] })),
      ]);
      setBalance(balRes.data);
      setPoints(ptRes.data);
      setReports(repRes.data);
      setLeaves(lvRes.data);
      setPendingOncall(pendRes.data);
      setMyOncall(oncRes.data);
      setExpenseClaims(claimsRes.data || []);
      setSites(sitesRes.data || []);
      setMyDocs(docsRes.data || []);
      setHoursConfirmations(hoursRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [eid]);

  const handleRespondOncall = async (id, accepted) => {
    try {
      const notes = !accepted ? prompt('Motivo (opzionale):') || '' : '';
      await api.put(`/oncall/${id}/respond`, { accepted, notes });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const uploadIdentityDoc = async (docType, title, file) => {
    if (!file) return;
    setUploadingId(docType);
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/personal-docs/upload?doc_type=${encodeURIComponent(docType)}&title=${encodeURIComponent(title)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Errore caricamento'); }
    finally { setUploadingId(null); }
  };

  const openNewClaim = () => {
    setClaimForm(EMPTY_CLAIM);
    setReceiptFile(null);
    setShowClaimDialog(true);
  };

  const handleSubmitClaim = async () => {
    if (!claimForm.amount || parseFloat(claimForm.amount) <= 0) {
      alert('Importo non valido');
      return;
    }
    if (!claimForm.description?.trim()) {
      alert('Inserisci una descrizione');
      return;
    }
    setSavingClaim(true);
    try {
      const payload = {
        ...claimForm,
        amount: parseFloat(claimForm.amount),
        site_id: claimForm.site_id || null,
      };
      const res = await api.post('/expense-claims', payload);
      // Upload scontrino se presente
      if (receiptFile && res.data?.id) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        await api.post(`/expense-claims/${res.data.id}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setShowClaimDialog(false);
      setClaimForm(EMPTY_CLAIM);
      setReceiptFile(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore creazione rimborso');
    } finally {
      setSavingClaim(false);
    }
  };

  const handleDeleteClaim = async (id) => {
    if (!window.confirm('Eliminare questa richiesta?')) return;
    try {
      await api.delete(`/expense-claims/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  if (loading) return <div className="animate-pulse text-center py-20 text-slate-400">Caricamento...</div>;

  if (!eid) return (
    <div className="text-center py-20">
      <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
      <h2 className="text-lg font-semibold text-slate-700">Account non collegato</h2>
      <p className="text-slate-500 mt-2">Il tuo account utente non e' collegato a una scheda dipendente. Contatta l'amministratore.</p>
    </div>
  );

  const maxPts = points?.punti_iniziali ?? 20;
  const currentPts = points?.current_points ?? maxPts;
  const ptsColor = points?.status === 'rosso' ? 'text-red-600' : points?.status === 'giallo' ? 'text-amber-600' : 'text-green-600';
  const ptsBg = points?.status === 'rosso' ? 'bg-red-50 border-red-200' : points?.status === 'giallo' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-area-page">
      <h1 className="text-2xl font-bold text-slate-900 tracking-tight">La Mia Area</h1>

      {/* Pending oncall banner */}
      {pendingOncall.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 shadow-md" data-testid="pending-oncall-area">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-800 flex items-center gap-2"><Moon size={16} /> Reperibilita da confermare ({pendingOncall.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {pendingOncall.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-white rounded p-2 border border-amber-200">
                <div className="text-sm"><strong>{p.date}</strong> - <Badge variant="outline">{p.oncall_type}</Badge> {p.notes && <span className="text-xs text-slate-500 ml-1">{p.notes}</span>}</div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleRespondOncall(p.id, true)}><Check size={14} className="mr-1" /> Si</Button>
                  <Button size="sm" variant="destructive" onClick={() => handleRespondOncall(p.id, false)}><X size={14} className="mr-1" /> No</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/leaves')}>
          <CardContent className="p-3 text-center">
            <Sun size={20} className="mx-auto text-blue-600 mb-1" />
            <p className="text-xs text-blue-700 font-medium">Ferie Residue</p>
            <p className="text-2xl font-bold text-blue-800">{balance?.ferie?.residuo ?? '-'}<span className="text-xs font-normal"> gg</span></p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/leaves')}>
          <CardContent className="p-3 text-center">
            <Clock size={20} className="mx-auto text-purple-600 mb-1" />
            <p className="text-xs text-purple-700 font-medium">Permessi Residui</p>
            <p className="text-2xl font-bold text-purple-800">{balance?.permessi?.ore_residuo ?? '-'}<span className="text-xs font-normal"> h</span></p>
          </CardContent>
        </Card>
        <Card className={`border cursor-pointer hover:shadow-md transition-shadow ${ptsBg}`} onClick={() => navigate(`/employees/${eid}`)}>
          <CardContent className="p-3 text-center">
            <ShieldCheck size={20} className={`mx-auto mb-1 ${ptsColor}`} />
            <p className="text-xs font-medium" style={{color: ptsColor === 'text-red-600' ? '#dc2626' : ptsColor === 'text-amber-600' ? '#d97706' : '#16a34a'}}>Punti Sicurezza</p>
            <p className={`text-2xl font-bold ${ptsColor}`}>{currentPts}<span className="text-xs font-normal"> /{maxPts}</span></p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/segnalazioni')}>
          <CardContent className="p-3 text-center">
            <ClipboardList size={20} className="mx-auto text-slate-600 mb-1" />
            <p className="text-xs text-slate-700 font-medium">Segnalazioni</p>
            <p className="text-2xl font-bold text-slate-800">{reports.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-rose-50 border-rose-200 cursor-pointer hover:shadow-md transition-shadow" onClick={openNewClaim} data-testid="rimborsi-card">
          <CardContent className="p-3 text-center">
            <Receipt size={20} className="mx-auto text-rose-600 mb-1" />
            <p className="text-xs text-rose-700 font-medium">Rimborsi</p>
            <p className="text-2xl font-bold text-rose-800">{expenseClaims.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/my-docs')}>
          <CardContent className="p-3 text-center">
            <FileText size={20} className="mx-auto text-emerald-600 mb-1" />
            <p className="text-xs text-emerald-700 font-medium">I Miei Documenti</p>
            <p className="text-2xl font-bold text-emerald-800">Apri</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/employees/${eid}?tab=corsi`)}>
          <CardContent className="p-3 text-center">
            <GraduationCap size={20} className="mx-auto text-amber-600 mb-1" />
            <p className="text-xs text-amber-700 font-medium">I Miei Corsi</p>
            <p className="text-2xl font-bold text-amber-800">Apri</p>
          </CardContent>
        </Card>
      </div>

      {/* Documenti Identità self-service */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><IdCard size={16} /> Documenti Identità</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">Aggiorna i tuoi documenti scattando una foto o caricando un file. Sono visibili solo a te e all'ufficio.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { type: 'carta_identita', label: "Carta d'Identità" },
              { type: 'codice_fiscale', label: 'Codice Fiscale' },
              { type: 'patente', label: 'Patente' },
            ].map(({ type, label }) => {
              const existing = myDocs.filter(d => d.doc_type === type).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
              return (
                <div key={type} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`identity-${type}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-800">{label}</p>
                    {existing ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">Caricato</Badge> : <Badge variant="outline" className="bg-slate-50 text-slate-500 text-[10px]">Mancante</Badge>}
                  </div>
                  {existing ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${existing.file_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 inline-flex items-center gap-1"><FileText size={13} /> Vedi ({formatDate(existing.created_at)})</a>
                  ) : (
                    <p className="text-xs text-slate-400">Nessun file</p>
                  )}
                  <div className="flex gap-2">
                    <label className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 cursor-pointer hover:bg-slate-50" data-testid={`identity-camera-${type}`}>
                      <Camera size={14} /> {uploadingId === type ? '...' : 'Foto'}
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && setPendingIdentity({ type, label, file: e.target.files[0] })} />
                    </label>
                    <label className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 cursor-pointer hover:bg-slate-50" data-testid={`identity-file-${type}`}>
                      <Upload size={14} /> File
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => e.target.files?.[0] && setPendingIdentity({ type, label, file: e.target.files[0] })} />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="ferie">Le Mie Ferie/Permessi</TabsTrigger>
          <TabsTrigger value="reperibilita">Le Mie Reperibilita</TabsTrigger>
          <TabsTrigger value="segnalazioni">Le Mie Segnalazioni</TabsTrigger>
          <TabsTrigger value="rimborsi" data-testid="tab-rimborsi">Rimborsi Spese ({expenseClaims.length})</TabsTrigger>
          <TabsTrigger value="ore" data-testid="tab-ore">
            Le Mie Ore
            {hoursConfirmations.filter(h => h.status === 'pending').length > 0 && (
              <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] px-1.5">{hoursConfirmations.filter(h => h.status === 'pending').length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ferie">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Dal</TableHead><TableHead>Al</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
                <TableBody>
                  {leaves.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-500">Nessuna richiesta</TableCell></TableRow>
                  ) : leaves.map(l => (
                    <TableRow key={l.id}>
                      <TableCell className="text-sm capitalize">{l.request_type}</TableCell>
                      <TableCell className="text-sm">{formatDate(l.start_date)}</TableCell>
                      <TableCell className="text-sm">{formatDate(l.end_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={l.status === 'approvata' ? 'bg-green-50 text-green-700' : l.status === 'rifiutata' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}>
                          {l.status === 'in_attesa' ? 'In Attesa' : l.status === 'approvata' ? 'Approvata' : 'Rifiutata'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reperibilita">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Stato</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
                <TableBody>
                  {myOncall.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-500">Nessuna reperibilita</TableCell></TableRow>
                  ) : myOncall.map(o => (
                    <TableRow key={o.id}>
                      <TableCell className="text-sm font-medium">{o.date}</TableCell>
                      <TableCell><Badge variant="outline">{o.oncall_type}</Badge></TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_MAP[o.status]?.c || ''}>{STATUS_MAP[o.status]?.l || o.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{o.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="segnalazioni">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Categoria</TableHead><TableHead>Gravita</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  {reports.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-500">Nessuna segnalazione</TableCell></TableRow>
                  ) : reports.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm">{formatDate(r.report_date)}</TableCell>
                      <TableCell><Badge variant="outline">{r.category}</Badge></TableCell>
                      <TableCell>
                        <Badge className={r.severity === 'critica' ? 'bg-red-100 text-red-800' : r.severity === 'alta' ? 'bg-orange-100 text-orange-800' : r.severity === 'media' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}>{r.severity}</Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[300px] truncate">{r.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rimborsi">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-slate-600 flex items-center gap-2">
                  <Receipt size={16} />
                  Carica gli scontrini e richiedi il rimborso. Verranno approvati dall'amministratore.
                </div>
                <Button size="sm" onClick={openNewClaim} data-testid="new-claim-btn">
                  <Plus size={14} className="mr-1" /> Nuova Richiesta
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Scontrino</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenseClaims.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Nessuna richiesta di rimborso</TableCell></TableRow>
                    ) : expenseClaims.map(c => (
                      <TableRow key={c.id}>
                        <TableCell className="text-sm">{formatDate(c.date)}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{c.category}</Badge></TableCell>
                        <TableCell className="text-sm font-medium text-right">{(c.amount || 0).toFixed(2)} EUR</TableCell>
                        <TableCell className="text-sm max-w-[250px] truncate">{c.description}</TableCell>
                        <TableCell>
                          {c.file_url ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${c.file_url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs">Visualizza</a>
                          ) : <span className="text-xs text-slate-400">Nessuno</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            c.status === 'approvato' ? 'bg-green-50 text-green-700 border-green-200' :
                            c.status === 'rimborsato' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            c.status === 'rifiutato' ? 'bg-red-50 text-red-700 border-red-200' :
                            'bg-amber-50 text-amber-700 border-amber-200'
                          }>
                            {c.status === 'in_attesa' ? 'In Attesa' : c.status === 'approvato' ? 'Approvato' : c.status === 'rimborsato' ? 'Rimborsato' : 'Rifiutato'}
                          </Badge>
                          {c.status === 'rifiutato' && c.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1">Motivo: {c.rejection_reason}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          {c.status === 'in_attesa' && (
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteClaim(c.id)} data-testid={`delete-claim-${c.id}`}>
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ore">
          <div className="space-y-3 mt-4">
            {hoursConfirmations.length === 0 ? (
              <Card className="border-slate-200 shadow-sm"><CardContent className="text-center py-6 text-slate-400 text-sm">Nessuna richiesta di conferma ore al momento.</CardContent></Card>
            ) : hoursConfirmations.map((item) => (
              <HoursConfirmationCard key={item.id} item={item} onSubmitted={load} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: Nuova Richiesta Rimborso */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuova Richiesta di Rimborso</DialogTitle><DialogDescription>Inserisci i dettagli della spesa e allega la foto/PDF dello scontrino.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Data *</Label>
                <Input type="date" value={claimForm.date} onChange={e => setClaimForm(f => ({ ...f, date: e.target.value }))} data-testid="claim-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Importo (EUR) *</Label>
                <Input type="number" step="0.01" min="0" value={claimForm.amount} onChange={e => setClaimForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" data-testid="claim-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoria *</Label>
                <Select value={claimForm.category} onValueChange={v => setClaimForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="claim-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cantiere</Label>
                <Select value={claimForm.site_id || '__none__'} onValueChange={v => setClaimForm(f => ({ ...f, site_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuno</SelectItem>
                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>N. Scontrino</Label>
              <Input value={claimForm.receipt_number} onChange={e => setClaimForm(f => ({ ...f, receipt_number: e.target.value }))} placeholder="opzionale" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione *</Label>
              <Textarea value={claimForm.description} onChange={e => setClaimForm(f => ({ ...f, description: e.target.value }))} placeholder="Es. Pranzo trasferta cantiere..." data-testid="claim-description" />
            </div>
            <div className="space-y-1.5">
              <Label>Scontrino (foto / PDF)</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" onChange={e => setReceiptFile(e.target.files?.[0] || null)} data-testid="claim-receipt" />
                {receiptFile && <span className="text-xs text-slate-500 truncate max-w-[200px]">{receiptFile.name}</span>}
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-1"><Upload size={11} /> Carica una foto dello scontrino</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDialog(false)} disabled={savingClaim}>Annulla</Button>
            <Button onClick={handleSubmitClaim} disabled={savingClaim} data-testid="claim-submit">
              {savingClaim ? 'Invio...' : 'Invia Richiesta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma documento identità con anteprima */}
      <Dialog open={!!pendingIdentity} onOpenChange={(o) => !o && setPendingIdentity(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{pendingIdentity?.label}</DialogTitle>
            <DialogDescription>Controlla l'anteprima e conferma il caricamento.</DialogDescription>
          </DialogHeader>
          <FilePreview file={pendingIdentity?.file} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingIdentity(null)}>Annulla</Button>
            <Button onClick={async () => { const p = pendingIdentity; setPendingIdentity(null); await uploadIdentityDoc(p.type, p.label, p.file); }} data-testid="identity-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
